import React from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Stack,
    Switch,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import ExpandLessOutlinedIcon from "@mui/icons-material/ExpandLessOutlined";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";

const WIDE_CARD = "@container (min-width: 1000px)";

// Tarjeta de una automatización en la lista, igual para todos los tipos: icono, nombre, Activa/Pausada, interruptor,
// detalle desplegable y menú. Lo propio del tipo (chips, subtítulo, detalle) sale de su entrada del registro. Un tipo que
// este frontend no conoce (backend más nuevo) se muestra sin acciones.
const AutomationCard = ({ item, type, busy = false, expanded = false, onToggleExpanded, onToggleEnabled, onOpenMenu }) => {
    const Icon = type?.icon || HelpOutlineOutlinedIcon;
    const CardSummary = type?.CardSummary;
    const CardDetails = type?.CardDetails;
    const statusChip = type?.statusChip?.(item);
    const canUpdate = !!type && item?.can?.update !== false;
    const subtitle = type ? type.subtitle(item) : "Este tipo de automatización no está disponible en esta versión.";
    const narrow = useMediaQuery((theme) => theme.breakpoints.down("sm"));

    return (
        <Paper
            variant="outlined"
            sx={{
                // Los chips van al lado del nombre según el ancho de la tarjeta, no de la ventana: con el asistente
                // abierto la tarjeta es angosta aunque la ventana sea grande.
                containerType: "inline-size",
                p: { xs: 1.5, md: 1.75 },
                borderRadius: 3,
                borderColor: item?.enabled ? "rgba(103, 58, 183, 0.22)" : "divider",
                background: "linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(247,248,252,0.94) 100%)",
                boxShadow: "0 10px 26px rgba(15, 23, 42, 0.04)",
                transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
                "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: "0 18px 44px rgba(15, 23, 42, 0.09)",
                },
            }}>
            <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Tooltip title={type?.label || "Automatización"}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2.5,
                                display: "grid",
                                placeItems: "center",
                                color: item?.enabled ? "primary.main" : "text.secondary",
                                bgcolor: item?.enabled ? "rgba(103, 58, 183, 0.08)" : "action.hover",
                                flex: "0 0 auto",
                            }}>
                            <Icon fontSize="small" />
                        </Box>
                    </Tooltip>

                    <Stack spacing={0.35} sx={{ minWidth: 0, flex: "1 1 240px" }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0, flexWrap: "wrap" }}>
                            <Typography variant="h6" sx={{ lineHeight: 1.15, minWidth: 0, m: 0 }} noWrap>
                                {item?.name || `Automatización #${item?.id}`}
                            </Typography>
                            <Chip
                                size="small"
                                label={item?.enabled ? "Activa" : "Pausada"}
                                color={item?.enabled ? "success" : "default"}
                                variant={item?.enabled ? "filled" : "outlined"}
                                sx={{ borderRadius: 999 }}
                            />
                            {statusChip && <Chip size="small" label={statusChip.label} color={statusChip.color} sx={{ borderRadius: 999 }} />}
                            {item?.created_via && (
                                <Tooltip title={`Creada desde ${item.created_via}`}>
                                    <Chip size="small" variant="outlined" icon={<SmartToyOutlinedIcon />} label={`vía ${item.created_via}`} sx={{ borderRadius: 999 }} />
                                </Tooltip>
                            )}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" noWrap>{subtitle}</Typography>
                    </Stack>

                    {CardSummary && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            useFlexGap
                            sx={{ display: "none", flexWrap: "wrap", gap: 0.75, flex: "0 1 auto", minWidth: 0, maxWidth: "60%", [WIDE_CARD]: { display: "flex" } }}>
                            <CardSummary item={item} />
                        </Stack>
                    )}

                    {type && (
                        <Stack direction="row" spacing={0.25} alignItems="center" flex="0 0 auto">
                            <Tooltip title={item?.enabled ? "Desactivar automatización" : "Activar automatización"}>
                                <span>
                                    <Switch
                                        size="small"
                                        color="success"
                                        checked={item?.enabled !== false}
                                        disabled={busy || !canUpdate}
                                        onChange={() => onToggleEnabled(item)}
                                    />
                                </span>
                            </Tooltip>
                            {busy && <CircularProgress size={16} />}
                            {CardDetails && (
                                <Tooltip title={expanded ? "Ocultar detalle" : "Ver detalle"}>
                                    <IconButton size="small" onClick={() => onToggleExpanded(item)}>
                                        {expanded ? <ExpandLessOutlinedIcon fontSize="small" /> : <ExpandMoreOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Acciones">
                                <span>
                                    <IconButton size="small" disabled={busy} onClick={(event) => onOpenMenu(event, item)}>
                                        <MoreVertOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>

                {CardSummary && (
                    <Stack direction="row" spacing={0.75} useFlexGap sx={{ display: "flex", flexWrap: "wrap", [WIDE_CARD]: { display: "none" } }}>
                        <CardSummary item={item} compact={narrow} />
                    </Stack>
                )}

                {CardDetails && (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <CardDetails item={item} />
                    </Collapse>
                )}
            </Stack>
        </Paper>
    );
};

export default AutomationCard;
